import { motion } from "framer-motion";
import { isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "../../../contexts/ToastContext.tsx";
import Icon from "../../../core/components/Icon.tsx";
import Separator from "../../../core/components/Separator.tsx";
import Carousel from "../../../core/data-display/Carousel.tsx";
import ModalImage from "../../../core/data-display/ModalImage.tsx";
import Loader from "../../../core/feedback/Loader.tsx";
import useService from "../../../hooks/useService.ts";
import { finalizarViagem } from "../../../services/ModuloGerenciador/Viagens.ts";
import "./FinalizarViagem.css";

interface FinalizarViagemFormProps {
	handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	imageName?: string;
	showCarousel: boolean;
	previewImages: { url: string; type: string }[];
	currentIndex: number;
	qtArquivosSelecionados?: number;
	isFinalizando?: boolean;
	id: number;
	handleFinalizarViagem: () => void;
	handleClickCarrouselImage: (index: number) => void;
	handleNext: () => void;
	handlePrev: () => void;
	onRemoveImage?: (index: number) => void;
}

function FinalizarViagemForm({
	handleInputChange,
	showCarousel,
	previewImages,
	currentIndex,
	handleClickCarrouselImage,
	handleNext,
	handlePrev,
	qtArquivosSelecionados = 0,
	isFinalizando = false,
	handleFinalizarViagem,
	id,
	imageName,
	onRemoveImage,
}: FinalizarViagemFormProps) {
	return (
		<motion.div
			className="finalizar-viagem-form"
			key="finalizar-viagem-form"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2 }}
		>
			<h1>{`Finalizar Viagem: ${id}`} </h1>
			<div className="file-upload-container">
				<label htmlFor="file-upload" className="custom-file-upload">
					Selecione Arquivos
				</label>
				<input
					id="file-upload"
					type="file"
					multiple
					accept="image/*,application/pdf"
					onChange={handleInputChange}
					style={{ display: "none" }}
				/>
				<Separator size="md" />
				<span className="file-upload-text">{`Limites: 10 arquivos e 10MB por arquivo`}</span>
				<span className="file-upload-text">{`Clique nas imagens para visualizar`}</span>
			</div>

			<div className="finalizar-viagem-carousel">
				<div className="carousel-container">
					<div className="uploaded-files-list">
						<span className="uploaded-file-name">{`Arquivos Selecionados: ${qtArquivosSelecionados}`}</span>
						<span className="uploaded-file-name">
							{imageName || "Nenhuma imagem selecionada"}
						</span>
					</div>
					<Separator size="md" />
					{showCarousel && (
						<Carousel
							handleNext={handleNext}
							handlePrev={handlePrev}
							currentIndex={currentIndex}
							images={previewImages}
							onSelectImage={handleClickCarrouselImage}
							onRemoveImage={onRemoveImage}
						/>
					)}
				</div>
				<motion.button
					className="finalizar-viagem-button"
					onClick={handleFinalizarViagem}
					disabled={!qtArquivosSelecionados || isFinalizando}
					whileHover={{
						scale: !qtArquivosSelecionados || isFinalizando ? 1 : 1.05,
					}}
					whileTap={{
						scale: !qtArquivosSelecionados || isFinalizando ? 1 : 0.95,
					}}
					transition={{ duration: 0.3 }}
					initial={{ opacity: 1 }}
					animate={{
						opacity: !qtArquivosSelecionados || isFinalizando ? 0.5 : 1,
					}}
				>
					Finalizar Viagem
				</motion.button>
			</div>
		</motion.div>
	);
}

function FinalizarViagem() {
	const MAX_FILES = 10;
	const MAX_FILE_BYTES = 10 * 1024 * 1024;

	const [files, setFiles] = useState<File[]>([]);
	const [previewImages, setPreviewImages] = useState<
		{ url: string; type: string }[]
	>([]);
	const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
		null
	);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isFormSent, setIsFormSent] = useState(false);
	const [loading, setLoading] = useState(false);

	const { showToast } = useToast();
	const { id } = useParams<{ id: string }>();

	const [isFinalizando, chamarFinalizaViagem] = useService(
		(formData: FormData) =>
			finalizarViagem({
				embarqueId: parseInt(id ? id : "0"),
				formFile: formData,
			}),
		{
			autoStart: false,
		}
	);

	const formatBytes = useCallback((bytes: number) => {
		const mb = bytes / (1024 * 1024);
		return `${mb.toFixed(2)}MB`;
	}, []);

	const handleCloseModalImage = useCallback(() => {
		setSelectedImageIndex(null);
	}, []);

	const handleNext = useCallback(() => {
		const newIndex =
			currentIndex === previewImages.length - 1 ? 0 : currentIndex + 1;
		setCurrentIndex(newIndex);
	}, [currentIndex, previewImages.length]);

	const handlePrev = useCallback(() => {
		const newIndex =
			currentIndex === 0 ? previewImages.length - 1 : currentIndex - 1;
		setCurrentIndex(newIndex);
	}, [currentIndex, previewImages.length]);

	const handleClickCarrouselImage = useCallback((index: number) => {
		setSelectedImageIndex(index);
	}, []);

	const handleFinalizarViagem = useCallback(() => {
		if (files.length === 0) {
			showToast(
				"Selecione pelo menos um arquivo para finalizar a viagem.",
				"error"
			);
			return;
		}

		setLoading(true);

		Promise.all(
			files.map((file) => {
				const formData = new FormData();
				formData.append("formFile", file);
				return chamarFinalizaViagem(formData);
			})
		)
			.then(() => {
				showToast("Viagem finalizada com sucesso!", "success");
				setFiles([]);
				setPreviewImages([]);
				setCurrentIndex(0);
				setSelectedImageIndex(null);
				setIsFormSent(true);
				setLoading(false);
			})
			.catch(() => {
				showToast("Erro ao finalizar viagem.", "error");
				setLoading(false);
			});
	}, [files, chamarFinalizaViagem, showToast]);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const inputFiles = e.target.files;
			if (!inputFiles) return;

			const newFiles = Array.from(inputFiles) as File[];

			e.target.value = "";

			const validBySize: File[] = [];
			const rejectedBySize: File[] = [];

			for (const f of newFiles) {
				if (f.size > MAX_FILE_BYTES) rejectedBySize.push(f);
				else validBySize.push(f);
			}

			if (rejectedBySize.length) {
				showToast(
					`Arquivos acima de 10MB foram ignorados: ${rejectedBySize
						.map((f) => `${f.name} (${formatBytes(f.size)})`)
						.join(", ")}`,
					"warning"
				);
			}

			setFiles((prevFiles) => {
				const combined = [...prevFiles, ...validBySize];

				if (combined.length > MAX_FILES) {
					showToast(
						`Máximo de ${MAX_FILES} arquivos permitido. Apenas os primeiros ${MAX_FILES} foram mantidos.`,
						"warning"
					);
					return combined.slice(0, MAX_FILES);
				}

				return combined;
			});
		},
		[showToast, formatBytes]
	);

	const handleRemoveImage = useCallback(
		(index: number) => {
			setFiles((prevFiles) => {
				const newFiles = prevFiles.filter((_, i) => i !== index);
				return newFiles;
			});
			if(currentIndex >= index && currentIndex > 0) {
				setCurrentIndex(currentIndex -1);
			}
		},
		[currentIndex]
	);

	useEffect(() => {
		if (files.length > 0) {
			const previews = files.map((file) => ({
				url: URL.createObjectURL(file),
				type: file.type,
			}));
			setPreviewImages(previews);
			setCurrentIndex(files.length - 1);

			return () => {
				previews.forEach((p) => URL.revokeObjectURL(p.url));
			};
		} else {
			setPreviewImages([]);
			setCurrentIndex(0);
		}
	}, [files]);

	const isLoading = useMemo(() => {
		return isFinalizando || !id || loading;
	}, [isFinalizando, id, loading]);

	return (
		<motion.div
			key="finalizar-viagem"
			layout
			className="finalizar-viagem-container"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.2 }}
		>
			<motion.div layout className="finalizar-viagem-body">
				{isLoading ? (
					<motion.div
						key="finalizar-viagem-loading"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.2 }}
						className="finalizar-viagem-loading"
					>
						<Loader size="lg" />
						<Separator size="md" />
						<span>Carregando...</span>
					</motion.div>
				) : isFormSent ? (
					<motion.div
						key="finalizar-viagem-finalizado"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.2 }}
						className="finalizar-viagem-finalizado"
					>
						<Icon name="CircleCheck" size="2xl" theme="green" />
						<Separator size="md" />
						<span>Viagem finalizada com sucesso!</span>
						<Separator size="md" />
						<span>Você pode fechar esta janela agora.</span>
					</motion.div>
				) : (
					<FinalizarViagemForm
						key="finalizar-viagem-form-container"
						handleInputChange={handleInputChange}
						imageName={files[currentIndex]?.name}
						showCarousel={!!previewImages.length}
						onRemoveImage={handleRemoveImage}
						previewImages={previewImages}
						currentIndex={currentIndex}
						handleClickCarrouselImage={handleClickCarrouselImage}
						handleNext={handleNext}
						handlePrev={handlePrev}
						qtArquivosSelecionados={files.length}
						isFinalizando={isFinalizando}
						handleFinalizarViagem={handleFinalizarViagem}
						id={id ? parseInt(id || "") : 0}
					/>
				)}
			</motion.div>

			<ModalImage
				isOpen={!isNil(selectedImageIndex)}
				image={previewImages[selectedImageIndex ?? 0]?.url ?? ""}
				onClose={handleCloseModalImage}
			/>
		</motion.div>
	);
}

export default FinalizarViagem;
